import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import { getBlogById } from "../../utils/blog";
import GenerateBlogLeft from "../../components/GenerateBlog/GenerateBlogLeft";
import { toast } from "react-toastify";
import { downloadBlogAsDocx } from "../../utils/docxHelper";
import { LiaCommentDots } from "react-icons/lia";
import CommentBox from "../../components/Common/CommentBox";
import { commentService } from "../../services/commentService";
import { useAuth } from "../../components/Context/authContext";
import "./BlogPreview.css";

const BlogPreview = () => {
  const { blogId } = useParams();
  const location = useLocation();
  const { textColor, viewMode: initialViewMode } = location.state || {};
  const { user: currentUser } = useAuth();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(initialViewMode || "desktop");
  
  // Comment system state
  const previewRef = useRef(null);
  const [mouseY, setMouseY] = useState(window.innerHeight / 2);
  const [insidePreview, setInsidePreview] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [comments, setComments] = useState([]);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [commentPosition, setCommentPosition] = useState(null);

  useEffect(() => {
    setLoading(true);
    getBlogById(blogId)
      .then((data) => {
        setBlog(data.blog);
        setLoading(false);
        // Load comments after blog is loaded
        loadComments();
      })
      .catch((err) => {
        setError("Failed to fetch blog preview.");
        setLoading(false);
        toast.error("Failed to fetch blog preview.");
      });
  }, [blogId]);

  // Load comments from backend
  const loadComments = async () => {
    try {
      const response = await commentService.getComments('Blog', blogId);
      const formattedComments = response.data.map(comment => ({
        id: comment.id,
        text: comment.content,
        position: comment.position_y,
        isSaved: true,
        user: comment.user,
        createdAt: comment.formatted_created_at
      }));
      setComments(formattedComments);
    } catch (error) {
      console.error('Failed to load comments:', error);
      // Don't show error toast for comments as it's not critical
    }
  };

  // Comment system handlers
  const handleCommentClick = () => {
    // Allow opening comment box for anonymous users too
    setCommentPosition(mouseY);
    setShowCommentBox(true);
  };

  const handleSaveComment = async (text) => {
    if (!text.trim() || !blogId) return;
    try {
      const commentData = {
        content: text.trim(),
        position_x: 0,
        position_y: commentPosition,
      };
      const response = await commentService.createComment('Blog', blogId, commentData);
      const created = response.data;
      setComments((prev) => [
        ...prev,
        {
          id: created.id,
          text: created.content,
          position: created.position_y,
          isSaved: true,
          user: created.user, // may be null
          createdAt: created.formatted_created_at
        },
      ]);
      setShowCommentBox(false);
    } catch (err) {
      console.error('Failed to save comment', err);
      toast.error('Failed to save comment. Please try again.');
    }
  };

  const handleEditComment = async (index, newText) => {
    const comment = comments[index];
    if (!comment.id) {
      // Handle local comments that haven't been saved yet
      const updatedComments = [...comments];
      updatedComments[index].text = newText;
      setComments(updatedComments);
      return;
    }
    
    try {
      const commentData = {
        content: newText.trim()
      };
      
      const response = await commentService.updateComment('Blog', blogId, comment.id, commentData);
      const updatedComments = [...comments];
      updatedComments[index] = {
        ...updatedComments[index],
        text: response.data.content,
        createdAt: response.data.formatted_created_at
      };
      setComments(updatedComments);
      toast.success('Comment updated successfully!');
    } catch (error) {
      console.error('Failed to update comment:', error);
      toast.error('Failed to update comment. Please try again.');
    }
  };

  const handleDeleteComment = async (index) => {
    const comment = comments[index];
    if (!comment.id) {
      // Handle local comments that haven't been saved yet
      setComments(comments.filter((_, i) => i !== index));
      return;
    }
    
    try {
      await commentService.deleteComment('Blog', blogId, comment.id);
      setComments(comments.filter((_, i) => i !== index));
      toast.success('Comment deleted successfully!');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment. Please try again.');
    }
  };

  // Mouse tracking for comment positioning
  useEffect(() => {
    const handleMouseMove = (e) => {
      const previewContainer = previewRef.current;
      if (previewContainer) {
        const { top, height } = previewContainer.getBoundingClientRect();
        let relativeY = e.clientY - top;
        let clampedY = Math.max(50, Math.min(relativeY, height - 50));
        setMouseY(clampedY);
      }
    };

    const handleMouseEnter = () => setInsidePreview(true);
    const handleMouseLeave = () => setInsidePreview(false);

    document.addEventListener("mousemove", handleMouseMove);
    const previewContainer = previewRef.current;
    if (previewContainer) {
      previewContainer.addEventListener("mouseenter", handleMouseEnter);
      previewContainer.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (previewContainer) {
        previewContainer.removeEventListener("mouseenter", handleMouseEnter);
        previewContainer.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  let parsedFinalBlog = null;
  if (blog?.final_blog_data) {
    parsedFinalBlog = blog.final_blog_data;
  } else if (blog?.final_blog) {
    try {
      parsedFinalBlog = JSON.parse(blog.final_blog);
    } catch (e) {
      parsedFinalBlog = null;
    }
  }
  
  // Check if blog is in HTML format
  const blogFormat = parsedFinalBlog?.metadata?.format || parsedFinalBlog?.format || blog?.format || "markdown";
  const htmlContent = parsedFinalBlog?.blog?.content_html || parsedFinalBlog?.content_html || null;
  const isHtmlFormat = blogFormat === "html" && htmlContent;
  
  const sections = parsedFinalBlog?.sections || [];
  const h1Title = parsedFinalBlog?.h1_title || parsedFinalBlog?.blog?.title || "";

  // Helper function to render content based on different formats
  // Add this new function to handle FAQ rendering
  // Add this new function to handle FAQ rendering
  const renderFAQContent = (content) => {
    // Handle different content types
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    // Split content by FAQ items - look for patterns like "1. **Question?**"
    const faqItems = contentString.split(/(?=\d+\.\s*\*\*)/);

    return (
      <div style={{ marginBottom: 16 }}>
        {faqItems.map((item, index) => {
          if (!item.trim()) return null;

          // Match FAQ pattern: 1. **Question?** followed by answer
          const match = item.match(/(\d+)\.\s*\*\*(.*?\?)\*\*\s*(.*)/s);
          if (match) {
            const [, number, question, answer] = match;
            return (
              <div
                key={index}
                style={{
                  marginBottom: 20,
                  backgroundColor: "#f8fafc",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 15 : 17,
                    color: "#E36250",
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "#E36250",
                      color: "white",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      marginRight: "12px",
                      flexShrink: 0,
                    }}
                  >
                    {number}
                  </span>
                  {question}
                </div>
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 13 : 15,
                    color: "#4b5563",
                    paddingLeft: 36,
                    lineHeight: 1.6,
                  }}
                >
                  {answer.trim()}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  // Add function to handle FAQ object format
  const renderFAQObjectContent = (content) => {
    return (
      <div style={{ marginBottom: 16 }}>
        {content.map((faq, index) => (
          <div
            key={index}
            style={{
              marginBottom: 20,
              backgroundColor: "#f8fafc",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: viewMode === "mobile" ? 15 : 17,
                color: "#E36250",
                marginBottom: 12,
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  backgroundColor: "#E36250",
                  color: "white",
                  borderRadius: "50%",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  marginRight: "12px",
                  flexShrink: 0,
                }}
              >
                Q
              </span>
              {faq.question}
            </div>
            <div
              style={{
                fontSize: viewMode === "mobile" ? 13 : 15,
                color: "#4b5563",
                paddingLeft: 40,
                lineHeight: 1.6,
              }}
            >
              {faq.answer}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Add function to handle Myths content
  const renderMythsContent = (content) => {
    if (typeof content === 'string') {
      // Parse numbered myths format like "1. **Does Shilajit Expire?**: Yes, Shilajit does..."
      const myths = content.split(/(?=\d+\.\s*\*\*)/);
      return (
        <div style={{ marginBottom: 16 }}>
          {myths.map((myth, index) => {
            if (!myth.trim()) return null;
            const match = myth.match(/(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/s);
            if (match) {
              const [, number, question, answer] = match;
              return (
                <div key={index} style={{ 
                  marginBottom: 16, 
                  padding: '16px', 
                  backgroundColor: '#fef3f2', 
                  borderRadius: '8px', 
                  border: '1px solid #fecaca', 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
                }}>
                  <div style={{ 
                    fontWeight: 600, 
                    fontSize: viewMode === "mobile" ? 15 : 17, 
                    color: '#dc2626', 
                    marginBottom: '8px', 
                    display: 'flex', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ 
                      backgroundColor: '#dc2626', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: '24px', 
                      height: '24px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '12px', 
                      marginRight: '12px',
                      flexShrink: 0
                    }}>
                      {number}
                    </span>
                    {question}
                  </div>
                  <div style={{ 
                    fontSize: viewMode === "mobile" ? 13 : 15, 
                    color: '#4b5563', 
                    lineHeight: 1.6, 
                    paddingLeft: '36px' 
                  }}>
                    {answer.trim()}
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }
    return null;
  };

  // Update the renderContent function - fix the recursive call issue
  const renderContent = (content) => {
    if (!content) return null;

    // Handle null/undefined content
    if (content === null || content === undefined) {
      return null;
    }



    // Handle FAQ object format first (before converting to string)
    if (Array.isArray(content) && content.length > 0) {
      // Check if it's FAQ format by looking at the first few items
      const isFAQFormat = content.slice(0, 3).every(item => 
        item && typeof item === 'object' && item.question && item.answer
      );
      if (isFAQFormat) {
        return renderFAQObjectContent(content);
      }
      
      // Handle array of strings or mixed content
      if (content.every(item => typeof item === 'string')) {
        return renderArrayContent(content);
      }
      
      // Handle mixed array content
      return renderMixedArrayContent(content);
    }

    // Handle object format (non-array objects)
    if (typeof content === 'object' && content !== null && !Array.isArray(content)) {
      return renderObjectContent(content);
    }

    // Handle Myths content format
    if (typeof content === 'string' && content.includes('**') && content.match(/\d+\.\s*\*\*.*?\*\*:\s*/)) {
      return renderMythsContent(content);
    }

    // Handle different content types
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }

    // Check if content contains table format (pipe-separated values)
    const hasTable =
      contentString.split("\n").filter((line) => line.includes("|")).length >= 2;

    if (hasTable) {
      return renderTableContent(contentString);
    }

    // Check if content contains numbered Q&A format like "1. **Question?**"
    if (contentString.includes("**") && contentString.match(/\d+\.\s*\*\*.*?\?\*\*/)) {
      return renderFAQContent(contentString); // ✅ Call the correct FAQ function
    }

    // Check if content contains traditional Q&A format
    if (contentString.includes("Q:") && contentString.includes("A:")) {
      return renderTraditionalQAContent(contentString);
    }

    // Check if content contains numbered list with bold headings
    if (contentString.match(/^\d+\.\s*\*\*.*?\*\*/m)) {
      return renderNumberedListContent(contentString);
    }

    // Check if content contains bullet points with bold headings
    if (contentString.match(/^-\s*\*\*.*?\*\*/m)) {
      return renderBulletListContent(contentString);
    }

    // Default: render as paragraphs
    return renderParagraphContent(contentString);
  };

  // New function to handle array content (array of strings)
  const renderArrayContent = (contentArray) => {
    // Check if this is a FAQ array (numbered questions with bold text)
    const isFAQArray = contentArray.length > 0 && 
      contentArray.every(item => 
        typeof item === 'string' && 
        item.match(/^\d+\.\s*\*\*.*?\*\*\s*/)
      );
    
    if (isFAQArray) {
      return (
        <div style={{ marginBottom: 16, textAlign: 'left' }}>
          {contentArray.map((item, index) => {
            // Parse FAQ item: "1. **Question?** Answer text"
            const faqMatch = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*\s*(.*)/);
            if (faqMatch) {
              const [, number, question, answer] = faqMatch;
              return (
                <div key={index} style={{ 
                  marginBottom: '20px', 
                  padding: '20px', 
                  backgroundColor: '#f8fafc', 
                  borderRadius: '12px', 
                  border: '1px solid #e2e8f0', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  textAlign: 'left'
                }}>
                  <div style={{ 
                    fontWeight: 600, 
                    fontSize: viewMode === "mobile" ? 14 : 16, 
                    color: '#E36250', 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    textAlign: 'left'
                  }}>
                    <span style={{ 
                      backgroundColor: '#E36250', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: '28px', 
                      height: '28px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '14px', 
                      marginRight: '12px',
                      flexShrink: 0
                    }}>
                      {number}
                    </span>
                    {question}
                  </div>
                  {answer && (
                    <div style={{ 
                      fontSize: viewMode === "mobile" ? 13 : 15, 
                      color: '#4b5563', 
                      lineHeight: '1.6', 
                      paddingLeft: '40px',
                      textAlign: 'left'
                    }}>
                      {answer}
                    </div>
                  )}
                </div>
              );
            }
            
            // Fallback for non-FAQ items
            return (
              <div key={index} style={{ marginBottom: 12 }}>
                <p style={{
                  fontSize: viewMode === "mobile" ? 14 : 16,
                  color: "#374151",
                  lineHeight: 1.6,
                  marginBottom: 8,
                  textAlign: 'left',
                }}>
                  {item.trim()}
                </p>
              </div>
            );
          })}
        </div>
      );
    }
    
    // Regular array content (non-FAQ)
    return (
      <div style={{ marginBottom: 16, textAlign: 'left' }}>
        {contentArray.map((item, index) => (
          <div key={index} style={{ marginBottom: 12 }}>
            {typeof item === 'string' ? (
              <p style={{
                fontSize: viewMode === "mobile" ? 14 : 16,
                color: "#374151",
                lineHeight: 1.6,
                marginBottom: 8,
                textAlign: 'left',
              }}>
                {item.trim()}
              </p>
            ) : (
              <div style={{
                fontSize: viewMode === "mobile" ? 14 : 16,
                color: "#6b7280",
                fontStyle: "italic",
                padding: "8px",
                backgroundColor: "#f9fafb",
                borderRadius: "4px",
                border: "1px solid #e5e7eb",
              }}>
                {JSON.stringify(item)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // New function to handle mixed array content
  const renderMixedArrayContent = (contentArray) => {
    return (
      <div style={{ marginBottom: 16, textAlign: 'left' }}>
        {contentArray.map((item, index) => (
          <div key={index} style={{ marginBottom: 16 }}>
            {renderContent(item)}
          </div>
        ))}
      </div>
    );
  };

  // New function to handle object content
  const renderObjectContent = (contentObject) => {
    const entries = Object.entries(contentObject);
    
    return (
      <div style={{ marginBottom: 16, textAlign: 'left' }}>
        {entries.map(([key, value], index) => (
          <div key={index} style={{ 
            marginBottom: 16, 
            padding: '16px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px', 
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              fontWeight: 600,
              fontSize: viewMode === "mobile" ? 14 : 16,
              color: "#1f2937",
              marginBottom: 8,
              textTransform: "capitalize",
            }}>
              {key.replace(/_/g, ' ')}
            </div>
            <div>
              {typeof value === 'string' ? (
                <p style={{
                  fontSize: viewMode === "mobile" ? 13 : 15,
                  color: "#4b5563",
                  lineHeight: 1.6,
                  margin: 0,
                  textAlign: 'left',
                }}>
                  {value}
                </p>
              ) : Array.isArray(value) ? (
                renderArrayContent(value)
              ) : typeof value === 'object' ? (
                <div style={{
                  fontSize: viewMode === "mobile" ? 12 : 14,
                  color: "#6b7280",
                  fontFamily: "monospace",
                  backgroundColor: "#f3f4f6",
                  padding: "8px",
                  borderRadius: "4px",
                  overflow: "auto",
                }}>
                  {JSON.stringify(value, null, 2)}
                </div>
              ) : (
                <span style={{
                  fontSize: viewMode === "mobile" ? 13 : 15,
                  color: "#4b5563",
                }}>
                  {String(value)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTableContent = (content) => {
    // Handle different content types
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const lines = contentString.split("\n").filter((line) => line.trim());
    const tableLines = lines.filter((line) => line.includes("|"));

    if (tableLines.length === 0) return renderParagraphContent(content);

    // Find header line - look for the first line with pipes that's not a separator
    const headerLine = tableLines.find(
      (line) => line.includes("|") && !line.match(/^\s*\|[\s\-\|]+\|\s*$/)
    );

    if (!headerLine) return renderParagraphContent(contentString);

    const headers = headerLine
      .split("|")
      .map((h) => h.trim())
      .filter((h) => h);

    // Find separator line (if exists)
    const separatorLineIndex = tableLines.findIndex((line) =>
      line.match(/^\s*\|[\s\-\|]+\|\s*$/)
    );

    // Find data rows - exclude header line and separator lines
    const dataRows = tableLines.filter(
      (line, index) =>
        line !== headerLine &&
        !line.match(/^\s*\|[\s\-\|]+\|\s*$/) && // Skip separator lines
        line.includes("|") &&
        line.split("|").filter((cell) => cell.trim()).length >= 2 // Ensure it has actual data
    );

    // Find non-table content (content before table and after table)
    const nonTableContent = [];
    let inTable = false;

    for (const line of lines) {
      if (line.includes("|")) {
        inTable = true;
      } else if (inTable && line.trim()) {
        // Content after table
        nonTableContent.push({ text: line, position: "after" });
      } else if (!inTable && line.trim()) {
        // Content before table
        nonTableContent.push({ text: line, position: "before" });
      }
    }

    const beforeTableContent = nonTableContent.filter(
      (item) => item.position === "before"
    );
    const afterTableContent = nonTableContent.filter(
      (item) => item.position === "after"
    );

    return (
      <div style={{ marginBottom: 16 }}>
        {/* Render content before table */}
        {beforeTableContent.map((item, index) => {
          const line = item.text;
          if (
            line.includes("**") &&
            (line.includes("Comparative") || line.includes("Analysis"))
          ) {
            return (
              <h3
                key={`before-${index}`}
                style={{
                  fontSize: viewMode === "mobile" ? 16 : 18,
                  color: "#1f2937",
                  fontWeight: 600,
                  marginBottom: 16,
                  marginTop: 8,
                }}
              >
                {line.replace(/\*\*/g, "")}
              </h3>
            );
          }
          return (
            <p
              key={`before-${index}`}
              style={{
                fontSize: viewMode === "mobile" ? 14 : 16,
                color: "#374151",
                lineHeight: 1.6,
                marginBottom: 12,
              }}
            >
              {line}
            </p>
          );
        })}

        {/* Render table */}
        <div
          style={{
            overflowX: "auto",
            marginBottom: 16,
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            backgroundColor: "#f9fafb",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: viewMode === "mobile" ? 12 : 14,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#E36250", color: "white" }}>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    style={{
                      padding: viewMode === "mobile" ? "8px" : "12px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: viewMode === "mobile" ? 12 : 14,
                    }}
                  >
                    {header.replace(/\*\*/g, "")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, rowIndex) => {
                const cells = row
                  .split("|")
                  .map((cell) => cell.trim())
                  .filter((cell) => cell);

                // Skip rows that don't have enough cells
                if (cells.length < 2) return null;

                return (
                  <tr
                    key={rowIndex}
                    style={{
                      backgroundColor:
                        rowIndex % 2 === 0 ? "#ffffff" : "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {cells.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        style={{
                          padding: viewMode === "mobile" ? "8px" : "12px",
                          verticalAlign: "top",
                          borderRight:
                            cellIndex < cells.length - 1
                              ? "1px solid #e5e7eb"
                              : "none",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: cellIndex === 0 ? 600 : 400,
                            color: cellIndex === 0 ? "#1f2937" : "#4b5563",
                          }}
                        >
                          {cell.replace(/\*\*/g, "")}
                        </span>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Render content after table */}
        {afterTableContent.map((item, index) => (
          <p
            key={`after-${index}`}
            style={{
              fontSize: viewMode === "mobile" ? 14 : 16,
              color: "#374151",
              lineHeight: 1.6,
              marginBottom: 12,
            }}
          >
            {item.text}
          </p>
        ))}
      </div>
    );
  };

  const renderTraditionalQAContent = (content) => {
    // Handle different content types
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const qaItems = contentString.split(/\n\s*\n/).filter((item) => item.trim());

    return (
      <div style={{ marginBottom: 16 }}>
        {qaItems.map((item, index) => {
          const lines = item.split("\n").filter((line) => line.trim());
          const question = lines.find((line) => line.startsWith("Q:"));
          const answer = lines.find((line) => line.startsWith("A:"));

          if (question && answer) {
            return (
              <div key={index} style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 14 : 16,
                    color: "#2563eb",
                    marginBottom: 4,
                  }}
                >
                  {question.replace("Q:", "").trim()}
                </div>
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 13 : 15,
                    color: "#444",
                    paddingLeft: 16,
                    borderLeft: "3px solid #e5e7eb",
                  }}
                >
                  {answer.replace("A:", "").trim()}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const renderNumberedListContent = (content) => {
    // Handle different content types
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const items = contentString.split(/(?=\d+\.\s*\*\*)/);

    return (
      <div style={{ marginBottom: 16 }}>
        {items.map((item, index) => {
          if (!item.trim()) return null;

          const match = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/s);
          if (match) {
            const [, number, title, description] = match;
            return (
              <div key={index} style={{ 
                marginBottom: 16, 
                padding: '16px', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '8px', 
                border: '1px solid #bae6fd',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 15 : 17,
                    color: "#0369a1",
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ 
                    backgroundColor: '#0369a1', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '24px', 
                    height: '24px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '12px', 
                    marginRight: '12px',
                    flexShrink: 0
                  }}>
                    {number}
                  </span>
                  {title}
                </div>
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 13 : 15,
                    color: "#4b5563",
                    paddingLeft: 36,
                    lineHeight: 1.6,
                  }}
                >
                  {description.trim()}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const renderBulletListContent = (content) => {
    // Handle different content types
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    // Split by lines first to handle the content properly
    const lines = contentString.split("\n").filter((line) => line.trim());
    const items = [];
    let currentItem = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check if this line starts a new bullet point with bold heading
      const bulletMatch = line.match(/^-\s*\*\*(.*?)\*\*:?\s*(.*)?$/);

      if (bulletMatch) {
        // Save previous item if exists
        if (currentItem) {
          items.push(currentItem);
        }

        // Start new item
        const [, title, initialDescription] = bulletMatch;
        currentItem = {
          title: title.trim(),
          description: initialDescription ? initialDescription.trim() : "",
        };
      } else if (currentItem && line.trim()) {
        // This is a continuation of the current item's description
        if (currentItem.description) {
          currentItem.description += " " + line.trim();
        } else {
          currentItem.description = line.trim();
        }
      } else if (!line.match(/^-\s*\*\*/) && line.trim()) {
        // This might be a standalone paragraph or heading
        if (!currentItem) {
          currentItem = {
            title: "",
            description: line.trim(),
          };
        }
      }
    }

    // Don't forget the last item
    if (currentItem) {
      items.push(currentItem);
    }

    // If no proper bullet items found, fall back to paragraph rendering
    if (items.length === 0 || items.every((item) => !item.title)) {
      return renderParagraphContent(content);
    }

    return (
      <div style={{ marginBottom: 16 }}>
        {items.map((item, index) => {
          if (!item.title && !item.description) return null;

          return (
            <div key={index} style={{ marginBottom: 16 }}>
              {item.title && (
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 15 : 17,
                    color: "#dc2626",
                    marginBottom: 4,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ marginRight: 8 }}>•</span>
                  {item.title}
                </div>
              )}
              {item.description && (
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 13 : 15,
                    color: "#4b5563",
                    paddingLeft: item.title ? 20 : 0,
                    lineHeight: 1.6,
                  }}
                >
                  {item.description}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderParagraphContent = (content) => {
    // Handle different content types
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const paragraphs = contentString.split("\n").filter((p) => p.trim());

    return (
      <div style={{ marginBottom: 16, textAlign: 'left' }}>
        {paragraphs.map((paragraph, index) => (
          <p
            key={index}
            style={{
              fontSize: viewMode === "mobile" ? 14 : 16,
              color: "#374151",
              lineHeight: 1.6,
              marginBottom: 12,
              textAlign: 'left',
            }}
          >
            {paragraph.trim()}
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div style={{ padding: 40 }}>Loading blog preview...</div>;
  }
  if (error) {
    return <div style={{ color: "red", padding: 40 }}>{error}</div>;
  }
  if (!blog) {
    return <div style={{ color: "red", padding: 40 }}>No blog found.</div>;
  }
  if (!blog.final_blog_data) {
    return (
      <div style={{ color: "red", padding: 40 }}>
        No blog content available. Please generate the blog before previewing.
        <br />
        <button onClick={() => window.history.back()} style={{ marginTop: 16 }}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="blog-preview-main" style={{ textAlign: 'left', width: '100%' }}>
      {/* Top Bar */}
      <div className="preview-header">
        <div
          className="preview-header-back"
          onClick={() => window.history.back()}
        >
          &lt; Back
        </div>
        <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">
          <div className="d-flex justify-content-center align-items-center">
            <button
              className={`btn ${viewMode === "desktop" ? "active" : ""}`}
              onClick={() => setViewMode("desktop")}
            >
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 24 24"
                height="20"
                width="20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M20 17.722c.595-.347 1-.985 1-1.722V5c0-1.103-.897-2-2-2H5c-1.103 0-2 .897-2 2v11c0 .736.405 1.375 1 1.722V18H2v2h20v-2h-2v-.278zM5 16V5h14l.002 11H5z"></path>
              </svg>
            </button>
            |
            <button
              className={`btn ${viewMode === "mobile" ? "active" : ""}`}
              onClick={() => setViewMode("mobile")}
            >
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 24 24"
                height="20"
                width="20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17 2H7c-1.103 0-2 .897-2 2v16c0 1.103.897 2 2 2h10c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM7 16.999V5h10l.002 11.999H7z"></path>
              </svg>
            </button>
          </div>
        </div>
        <div className="btns d-flex justify-content-end gap-3">
          <div>
            <button
              className="btn bgprimary rounded"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success("Link copied to clipboard!");
              }}
            >
              Share
            </button>
          </div>
        </div>
      </div>
      {/* Blog Content with responsive container based on viewMode */}
      <div className="d-flex justify-content-start p-4" style={{ justifyContent: 'flex-start', textAlign: 'left' }}>
        <div
          ref={previewRef}
          className={`blog-content blog-content-container ${viewMode}`}
          style={{
            width: viewMode === "mobile" ? "375px" : "100%",
            maxWidth: viewMode === "mobile" ? "375px" : "1000px",
            border: viewMode === "mobile" ? "12px solid #333" : "none",
            borderRadius: viewMode === "mobile" ? "30px" : "0",
            height: viewMode === "mobile" ? "667px" : "auto",
            overflow: "visible",
            backgroundColor: "white",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            transition: "all 0.3s ease",
            padding: "20px",
            position: "relative",
            textAlign: "left",
          }}
        >
          {/* Comment System */}
          {!showCommentBox && (
            <div
              style={{
                position: "absolute",
                right: "-60px",
                top: `${mouseY}px`,
                zIndex: 1000,
                cursor: "pointer",
                fontSize: "24px",
                color: isHovered ? "#D04734" : "#666",
                backgroundColor: isHovered ? "rgba(208, 71, 52, 0.1)" : "rgba(0, 0, 0, 0.1)",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isHovered ? "0 4px 12px rgba(208, 71, 52, 0.3)" : "0 2px 8px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease",
                animation: isHovered ? "heartbeat 1.5s ease-in-out infinite" : "none",
                border: isHovered ? "2px solid #D04734" : "1px solid rgba(0,0,0,0.1)",
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={(e) => {
                e.stopPropagation();
                handleCommentClick();
              }}
              aria-label="Add comment"
              title={"Add comment"}
            >
              {isHovered ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 45 44"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M43.18 21.976C43.18 33.5312 33.708 42.952 22.09 42.952C17.576 42.952 13.358 41.5536 9.88 39.1248L1 42.952L4.848 34.12C2.406 30.7344 1 26.5392 1 21.976C1 10.4208 10.472 1 22.09 1C33.708 1 43.18 10.4208 43.18 21.976Z"
                    stroke="#D04734"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="15" cy="22" r="2" fill="#D04734" />
                  <circle cx="22" cy="22" r="2" fill="#D04734" />
                  <circle cx="29" cy="22" r="2" fill="#D04734" />
                </svg>
              ) : (
                <LiaCommentDots />
              )}
            </div>
          )}
          
          {/* Existing Comments */}
          {comments.map((comment, index) => (
            <CommentBox
              key={index}
              position={comment.position}
              commentText={comment.text}
              isSaved={comment.isSaved}
              onSave={(text) => handleEditComment(index, text)}
              onEdit={(newText) => handleEditComment(index, newText)}
              onDelete={() => handleDeleteComment(index)}
              user={comment.user}
              canModify={!!currentUser}
            />
          ))}

          {/* New Comment Box */}
          {showCommentBox && (
            <CommentBox
              position={commentPosition}
              onSave={handleSaveComment}
              onCancel={() => setShowCommentBox(false)}
              user={currentUser}
            />
          )}
          {/* Main H1 Title - Only show for non-HTML format or if HTML doesn't have H1 */}
          {h1Title && !isHtmlFormat && (
            <h1
              style={{
                fontSize: viewMode === "mobile" ? 24 : 32,
                fontWeight: 700,
                color: "#111827",
                marginBottom: 32,
                lineHeight: 1.2,
                textAlign: "left",
              }}
            >
              {h1Title}
            </h1>
          )}

          {/* Render HTML content if format is HTML */}
          {isHtmlFormat && htmlContent ? (
            <div 
              className="blog-html-content-preview"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              style={{
                fontSize: viewMode === "mobile" ? 14 : 16,
                lineHeight: 1.6,
                color: "#374151",
              }}
            />
          ) : (
            sections.map((section, idx) => (
            <div
              key={idx}
              className="blog-section"
              style={{
                marginBottom: 40,
                borderBottom:
                  idx < sections.length - 1 ? "1px solid #f3f4f6" : "none",
                paddingBottom: idx < sections.length - 1 ? 32 : 0,
              }}
            >
              {section.image && (
                <img
                  src={section.image}
                  alt={section.section_title || ""}
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    marginBottom: 16,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                />
              )}

              {section.section_title && (
                <h2
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 20 : 26,
                    marginBottom: 16,
                    color: "#1f2937",
                    borderLeft: "4px solid #E36250",
                    paddingLeft: 16,
                    lineHeight: 1.3,
                  }}
                >
                  {section.section_title}
                </h2>
              )}

              {section.content && (
                <div style={{ marginBottom: 16 }}>
                  {renderContent(section.content)}
                </div>
              )}

              {/* Render tags */}
              {section.tags && (
                <div style={{ margin: "16px 0" }}>
                  {section.tags.map((tag, i) => (
                    <span
                      key={i}
                      style={{
                        background: "#dbeafe",
                        color: "#dc2626",
                        borderRadius: 16,
                        padding: "4px 12px",
                        marginRight: 8,
                        marginBottom: 4,
                        fontSize: viewMode === "mobile" ? 12 : 14,
                        fontWeight: 500,
                        display: "inline-block",
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Keywords integrated display */}
              {section.keywords_integrated && (
                <div
                  style={{
                    margin: "16px 0",
                    padding: "12px",
                    backgroundColor: "#f9fafb",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      fontSize: viewMode === "mobile" ? 12 : 14,
                      fontWeight: 600,
                      color: "#6b7280",
                      marginBottom: 8,
                    }}
                  >
                    Related Keywords:
                  </div>
                  <div>
                    {section.keywords_integrated.map((keyword, i) => (
                      <span
                        key={i}
                        style={{
                          background: "#f3f4f6",
                          color: "#374151",
                          borderRadius: 12,
                          padding: "2px 8px",
                          marginRight: 6,
                          marginBottom: 4,
                          fontSize: viewMode === "mobile" ? 11 : 13,
                          display: "inline-block",
                        }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested image or infographic info */}
              {section.suggested_image_or_infographic && (
                <div
                  style={{
                    margin: "16px 0",
                    padding: "12px",
                    backgroundColor: "#fef3c7",
                    borderRadius: "8px",
                    border: "1px solid #fbbf24",
                  }}
                >
                  <div
                    style={{
                      fontSize: viewMode === "mobile" ? 12 : 14,
                      fontWeight: 600,
                      color: "#92400e",
                      marginBottom: 4,
                    }}
                  >
                    💡 Suggested Visual:
                  </div>
                  <div
                    style={{
                      fontSize: viewMode === "mobile" ? 12 : 14,
                      color: "#a16207",
                      fontStyle: "italic",
                    }}
                  >
                    {section.suggested_image_or_infographic}
                  </div>
                </div>
              )}

              {/* Word count display */}
              {section.word_count && (
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 11 : 13,
                    color: "#9ca3af",
                    textAlign: "right",
                    marginTop: 8,
                  }}
                >
                  {section.word_count} words
                </div>
              )}
            </div>
          ))
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPreview;
